import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Plus, RefreshCw, Trash2 } from 'lucide-react';
import uapiVendorsService from '../services/uapiVendorsService';
import partnerService from '../services/partnerService';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import ConfirmModal from './shared/ConfirmModal';
import SearchSelect from './shared/SearchSelect';
import EmptyValue from './shared/EmptyValue';
import './OhipTransactionsPage.css';     // .otx-page / .otx-header / .otx-title — the admin page shell
import './OxiDequeueAdmin.css';         // .acp-table / .odq-clickable — the admin list table
import './OpenSearchAdmin.css';         // .os-msg-* — the inline message strip
import './TaskConfigurationPage.css';   // .empty-state / .loading-section

// Admin > Profiles > Vendors — the companies that integrate with us through the Universal API.
//
// A vendor is Thynk, or dailypoint: a business that holds one set of credentials and uses them across
// every property it has been given, which may span several chains under a partner. That is why it sits
// under Profiles beside Users and Partners rather than under a hotel — it is a party we deal with, not
// a property setting.
//
// WHAT A VENDOR ACTUALLY IS, in the data: a Universal API credential held at PARTNER level, with one
// uapi_hotel_access binding per property it may reach. There is no vendors table, deliberately — the
// identity, the reach, the permissions and the activity are already stored, and a second record of them
// would be a second answer to "who may call us".
//
// THE SECRET IS SHOWN ONCE. Creating a vendor mints an app client; the client secret exists in that one
// response and nowhere readable afterwards. So the dialog ends on the credentials rather than closing,
// and says plainly that this is the only time they will be visible.

/**
 * @param {number} [partnerId]  scope to one partner — the Partner Settings tab passes it, and then the
 *                              partner is implied rather than chosen: no Partner column, no picker on
 *                              the Add dialog. Without it the screen is every vendor on the platform.
 * @param {boolean} [embedded]  drop the page shell (<h1> and padding) so it sits inside a tab
 */
const VendorsPage = ({ partnerId = null, embedded = false }) => {
    const scoped = partnerId !== null && partnerId !== undefined && partnerId !== '';
    const [vendors, setVendors] = useState([]);
    // The systems a vendor may hold credentials for — marketplace.IS_VENDOR = 'Y' (V504), resolved by
    // the server so a stale client-side list cannot offer one that has been switched off.
    const [marketplaces, setMarketplaces] = useState([]);
    const [partners, setPartners] = useState([]);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    // The vendor a person has asked to delete, held until they confirm. Null the rest of the time.
    const [removing, setRemoving] = useState(null);
    const [removeError, setRemoveError] = useState(null);
    const [removeBusy, setRemoveBusy] = useState(false);

    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState({ name: '', partnerId: null, marketplaceId: null });
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await uapiVendorsService.list(scoped ? { partnerId } : {});
            setVendors(data.vendors || []);
            setMarketplaces(Array.isArray(data.marketplaces) ? data.marketplaces : []);
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Could not load vendors');
        } finally {
            setLoading(false);
        }
    }, [scoped, partnerId]);

    useEffect(() => { load(); }, [load]);

    // DELETING A VENDOR IS REVOKING ITS CREDENTIALS. There is no other way to take back a client id and
    // secret that has been handed to a company, so the row's bin is the revoke button as much as it is
    // the delete button — which is why the confirm below spells out what goes with it.
    //
    // A 409 is its own answer: the credential is doing a second job as somebody's backend credential and
    // deleting it would break their routes. That message is the server's, shown as it was written rather
    // than flattened into "could not delete".
    const handleRemove = useCallback(async () => {
        if (!removing) return;
        setRemoveBusy(true);
        setRemoveError(null);
        try {
            await uapiVendorsService.remove(removing.id);
            setRemoving(null);
            await load();
        } catch (e) {
            setRemoveError(e.response?.data?.message || e.message || 'Could not delete this vendor');
        } finally {
            setRemoveBusy(false);
        }
    }, [removing, load]);

    // Partners are only needed to create one, but they are small and a vendor cannot be created without
    // choosing one — fetching on open would put a spinner inside the dialog for no gain.
    useEffect(() => {
        if (scoped) return;          // the partner is settled; nothing to pick from
        partnerService.getAllPartners()
            .then(res => setPartners((res.data || res)?.partners || res.data || []))
            .catch(() => setPartners([]));
    }, [scoped]);

    const partnerItems = useMemo(() => (Array.isArray(partners) ? partners : []).map(p => ({
        id: p.id ?? p.PARTNERID,
        label: p.companyname || p.COMPANYNAME || `Partner ${p.id}`,
    })), [partners]);

    // `code` is the system key (SYSTEMURLPARAMETER), shown muted beside the name: somebody who knows a
    // system as THYNK types that, somebody who knows it as "Thynk" types that.
    const marketplaceItems = useMemo(() => (marketplaces || []).map(m => ({
        id: m.id,
        label: m.name,
        code: m.system_key || undefined,
    })), [marketplaces]);

    const shown = useMemo(() => {        const q = search.trim().toLowerCase();
        if (!q) return vendors;
        return vendors.filter(v => `${v.name || ''} ${v.partner_name || ''}`.toLowerCase().includes(q));
    }, [vendors, search]);

    const submit = async () => {
        setSaving(true);
        setSaveError(null);
        try {
            const res = await uapiVendorsService.create({
                name: form.name.trim(),
                partnerId: scoped ? partnerId : form.partnerId,
                marketplaceId: form.marketplaceId,
            });
            // STRAIGHT TO THE VENDOR, because creating it is the start of the job rather than the end:
            // credentials are issued on its Credentials tab and its users are added on the other one.
            // Nothing is shown here that would be worth stopping for — no secret exists yet.
            closeDialog();
            if (res.credentialId) {
                navigate(`/vendors/${res.credentialId}${scoped ? '?from=partner-settings' : ''}`);
                return;
            }
            await load();
        } catch (e) {
            setSaveError(e.response?.data?.message || e.message || 'Could not create the vendor');
        } finally {
            setSaving(false);
        }
    };

    // Opening the dialog starts from nothing chosen. A system left over from a cancelled attempt is a
    // default nobody picked, on the one field that decides what the credentials are for.
    const openDialog = () => {
        setForm({ name: '', partnerId: null, marketplaceId: marketplaceItems.length === 1 ? marketplaceItems[0].id : null });
        setSaveError(null);
        setAdding(true);
    };

    const closeDialog = () => {
        setAdding(false);
        setForm({ name: '', partnerId: null });
        setSaveError(null);
    };

    return (
        <div className={embedded ? 'otx-embedded' : 'otx-page'}>
            <div className="otx-header">
                <div>
                    {!embedded && <h1 className="otx-title">Vendors</h1>}
                    <p className="otx-subtitle">
                        Companies that call the Universal API — one set of credentials each, used across every
                        property they have been given. A vendor polls <code>/mq</code> for messages, or sends
                        them to us, over the chains and hotels its partner owns.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Button variant="secondary" onClick={load} disabled={loading}>
                        <RefreshCw className={`icon-sm ${loading ? 'spin' : ''}`} />
                        Refresh
                    </Button>
                    {/* Nothing to add a vendor FOR until a system is flagged as one. Said on the
                        button rather than discovered inside an empty dropdown. */}
                    <Button onClick={openDialog} disabled={!marketplaceItems.length}
                        title={marketplaceItems.length ? undefined
                            : 'No system is marked as a vendor system. Switch one on from its marketplace record (System Information > Vendor system).'}>
                        <Plus className="icon-sm" />
                        Add Vendor
                    </Button>
                </div>
            </div>

            {error && <div className="os-msg os-msg-error">{error}</div>}

            <div style={{ margin: '0 0 1rem' }}>
                <input
                    type="text"
                    className="form-input"
                    style={{ maxWidth: 360 }}
                    placeholder="Search vendors…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            {loading && !vendors.length ? (
                <div className="loading-section"><Spinner size="md" /><p>Loading vendors…</p></div>
            ) : !shown.length ? (
                <div className="empty-state">
                    <AlertCircle className="empty-icon" />
                    <p>
                        {search
                            ? 'No vendor matches that search.'
                            : 'No vendors yet. Add one to issue the credentials a company uses to reach the Universal API.'}
                    </p>
                </div>
            ) : (
                <table className="acp-table">
                    <thead>
                        <tr>
                            <th>Vendor</th>
                            <th>Users</th>
                            {!scoped && <th>Partner</th>}
                            <th>Properties</th>
                            <th>Modules</th>
                            <th aria-label="Actions" />
                        </tr>
                    </thead>
                    {/* ROWS ARE NOT CLICKABLE YET. There is no vendor detail page — binding properties
                        and granting modules still happen on the Universal API screens — and a row that
                        looks like a link and does nothing is worse than one that plainly is not. */}
                    <tbody>
                        {shown.map(v => (
                            // THE ROW IS THE CONTROL, not a blue link inside it. `.odq-clickable` is the admin list's own
                            // clickable-row style (cursor + hover tint) — the same one OxiDequeueAdmin uses — so a vendor
                            // list behaves and looks like every other list of records in the portal.
                            <tr
                                key={v.id}
                                className="odq-clickable"
                                onClick={() => navigate(`/vendors/${v.id}${scoped ? '?from=partner-settings' : ''}`)}
                                title={`Open ${v.name}`}
                            >
                                <td style={{ fontWeight: 600 }}>{v.name}</td>
                                {/* A vendor's users are added on its own screen, so a new one having none
                                    is the ordinary state rather than a fault — hence the muted "none". */}
                                <td>
                                    {v.users > 0
                                        ? `${v.users} user${v.users === 1 ? '' : 's'}`
                                        : <EmptyValue>none</EmptyValue>}
                                </td>
                                {!scoped && <td>{v.partner_name || <EmptyValue />}</td>}
                                <td>
                                    {v.properties > 0
                                        ? `${v.properties}${v.systems > 1 ? ` · ${v.systems} systems` : ''}`
                                        : <EmptyValue>none</EmptyValue>}
                                </td>
                                <td>{v.modules > 0 ? v.modules : <EmptyValue>none</EmptyValue>}</td>
                                {/* THE QUEUE IS NOT SHOWN HERE. "Waiting" and "last collected" describe the
                                    /mq queue, which is one marketplace's behaviour rather than something
                                    true of every vendor — this list is the companies, not their traffic.
                                    Both counts are still fetched: the delete confirm needs to say how many
                                    undelivered messages go with the credential. */}
                                {/* Deleting a vendor revokes its credentials — there is no other way to
                                    take them back. The confirm below names what goes with them. */}
                                <td style={{ textAlign: 'right' }}>
                                    <button
                                        type="button"
                                        className="acp-icon-btn"
                                        title={`Delete ${v.name}`}
                                        aria-label={`Delete ${v.name}`}
                                        onClick={(e) => {
                                            // The row opens the vendor; this does not. Without stopping it
                                            // the bin would open the record and then raise the confirm over
                                            // a page the person did not ask for.
                                            e.stopPropagation();
                                            setRemoveError(null);
                                            setRemoving(v);
                                        }}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '0.25rem' }}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {adding && (
                <ConfirmModal
                    title="Add vendor"
                    confirmLabel={saving ? 'Creating…' : 'Create'}
                    cancelLabel="Cancel"
                    onConfirm={() => {
                        if (saving) return;
                        if (!form.marketplaceId || !form.name.trim() || !(scoped || form.partnerId)) return;
                        submit();
                    }}
                    onCancel={() => { if (!saving) closeDialog(); }}
                >
                    <div style={{ textAlign: 'left' }}>
                        {/* WHICH SYSTEM FIRST. The credentials are issued FOR a system, so that is the
                            decision the rest of the form hangs off — and it is read before the name,
                            which only describes the company holding them.

                            The list is every marketplace flagged IS_VENDOR (V504), from the server. It
                            is a SearchSelect rather than a <select> because it is the control the rest
                            of the site uses for choosing one of a list, and a system is as likely to be
                            known by its key (THYNK) as by its name. */}
                        <label className="form-label" htmlFor="vendor-marketplace">System</label>
                        <SearchSelect
                            id="vendor-marketplace"
                            items={marketplaceItems}
                            value={form.marketplaceId}
                            onChange={(next) => setForm(f => ({ ...f, marketplaceId: next }))}
                            placeholder="Choose a system…"
                            searchPlaceholder="Search systems…"
                            emptyLabel="No system is marked as a vendor system."
                        />

                        <label className="form-label" htmlFor="vendor-name" style={{ marginTop: '0.75rem' }}>Name</label>
                        <input
                            id="vendor-name"
                            className="form-input"
                            value={form.name}
                            onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                            placeholder="Thynk"
                            autoFocus
                        />

                        {/* The partner owns the relationship, and the credential is held at that level —
                            which is what lets one set of credentials work across every chain and hotel
                            beneath it. On a partner's own settings it is already settled, so asking again
                            only invites choosing the wrong one. */}
                        {!scoped && <>
                            <label className="form-label" htmlFor="vendor-partner" style={{ marginTop: '0.75rem' }}>Partner</label>
                            <SearchSelect
                                id="vendor-partner"
                                items={partnerItems}
                                value={form.partnerId}
                                onChange={(next) => setForm(f => ({ ...f, partnerId: next }))}
                                placeholder="Choose a partner…"
                                searchPlaceholder="Search partners…"
                                emptyLabel="No partners available."
                            />
                        </>}

                        <p className="form-hint" style={{ marginTop: '0.75rem' }}>
                            This creates the vendor only. Its credentials are issued afterwards, on the
                            vendor's Credentials tab, so the client secret is shown at the moment somebody is
                            ready to hand it over rather than now.
                        </p>

                        {saveError && <div className="os-msg os-msg-error" style={{ marginTop: '0.75rem' }}>{saveError}</div>}
                    </div>
                </ConfirmModal>
            )}

            {/* WHAT GOES IS NAMED, because none of it comes back. The counts are the ones already on the
                row, so the confirm is describing exactly what the person is looking at. */}
            {removing && (
                <ConfirmModal
                    title={`Delete ${removing.name}?`}
                    confirmLabel={removeBusy ? 'Deleting…' : 'Delete vendor'}
                    danger
                    onConfirm={handleRemove}
                    onCancel={() => { if (!removeBusy) { setRemoving(null); setRemoveError(null); } }}
                >
                    <div>
                        <p style={{ marginTop: 0 }}>
                            This revokes the credentials {removing.name} uses to reach the Universal API. They
                            stop working immediately and cannot be restored — a vendor that needs access again
                            has to be issued a new client id and secret.
                        </p>
                        <p style={{ margin: '0 0 0.75rem' }}>Deleted with it:</p>
                        <ul style={{ margin: '0 0 0.75rem 1.25rem' }}>
                            <li>
                                {removing.properties > 0
                                    ? `${removing.properties} property binding${removing.properties === 1 ? '' : 's'}`
                                    : 'no property bindings'}
                            </li>
                            <li>
                                {removing.modules > 0
                                    ? `${removing.modules} module grant${removing.modules === 1 ? '' : 's'}`
                                    : 'no module grants'}
                            </li>
                            <li>
                                {removing.queued > 0
                                    ? `${removing.queued.toLocaleString()} message${removing.queued === 1 ? '' : 's'} still waiting to be collected`
                                    : 'nothing waiting in the queue'}
                            </li>
                            <li>
                                {removing.users > 0
                                    ? `${removing.users} user${removing.users === 1 ? '' : 's'} under this partner`
                                    : 'no users (it has none)'}
                            </li>
                        </ul>
                        <p style={{ margin: 0, color: '#6b7280' }}>
                            The record of what this vendor has already collected is kept.
                        </p>
                        {removeError && (
                            <div className="os-msg os-msg-error" style={{ marginTop: '0.75rem' }}>{removeError}</div>
                        )}
                    </div>
                </ConfirmModal>
            )}
        </div>
    );
};

export default VendorsPage;
